import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Container } from "@mui/material";
import {
  faBolt,
  faSyncAlt,
  faSms,
  faPalette,
  faWallet,
  faMoneyBill1,
  faHandBackFist,
  faQrcode,
  faHeadset,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { motion, useInView as useView } from "framer-motion";
import { useInView } from "react-intersection-observer";
// import Card3D from "../card/3d-card";
import Card3D from "../card/3d-card";
import { useRef } from "react";

export const Features: React.FC = () => {
  const ref = useRef(null);
  const isInView = useView(ref, { once: true });
  return (
    <div className="bg-[#eff6ff] md:pt-0 pt-12 pb-6 ">
      <Container className="flex flex-col gap-8 md:gap-12 items-center">
        <AnimatedHeading text="Why Diamedic?" />
        <AnimatedParagraphUp>
          <p className="mb-4">
            “Living with Type 1 Diabetes for over 20 years, I've experienced both
            mild and severe hypos. In most scenarios I've been lucky to receive
            help from those who knew what to do, but that's not always the case.
          </p>
          <p className="mb-4">
            Traditional medical cards lack crucial diabetic-specific details;
            information that could be lifesaving in an emergency. That's why I
            created the Diamedic card. I want to give people peace of mind, knowing their card can speak for them when they can't speak for themselves.”
          </p>
          <p className="text-right not-italic font-bold">- Calum, Founder</p>
        </AnimatedParagraphUp>
        <div className="grid grid-cols-1 md:grid-cols-3 md:gap-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <LandingPageCard icon={<FontAwesomeIcon icon={faBolt} size="7x" style={{ color: "orange" }} />} title="Instant Access" desc={<>The QR code can be scanned for <span className="font-bold text-main-blue">instant access</span> to critical medical details</>} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1.0, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <LandingPageCard icon={<FontAwesomeIcon icon={faSms} size="7x" style={{ color: "orange" }} />} title="SMS Notifications" desc={<>Notify your <span className="font-bold text-main-blue">emergency contacts</span> (if enabled) anytime your QR code is scanned</>} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 1.0, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <LandingPageCard icon={<FontAwesomeIcon icon={faSyncAlt} size="7x" style={{ color: "orange" }} />} title="Always Up-to-Date" desc={<>Update your <span className="font-bold text-main-blue">medical info</span> at any time without needing to replace your card</>} />
          </motion.div>
        </div>

        <motion.div className="flex flex-col md:flex-row gap-12 mt-8 items-center" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: "easeOut" }} viewport={{ once: true }}>
          <div className="flex flex-col gap-14 justify-evenly md:basis-1/3">
            <AnimatedSmallCard direction="left" icon={faPalette} title="Custom Colour" desc="Choose from a variety of colours that suit your style during checkout" align="text-start" />
            <AnimatedSmallCard direction="left" icon={faHandBackFist} title="Durable Material" desc="Crafted from durable PVC, your Diamedic card is sturdy, waterproof, and built to withstand most conditions" align="text-start" />
            <AnimatedSmallCard direction="left" icon={faQrcode} title="Easy Access" desc="Simply scan the QR code on any device to gain immediate access to emergency information" align="text-start" border={false} />
          </div>
          {/* <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 1.0, ease: "easeOut" }} viewport={{ once: true }}> */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 1.0, ease: "easeOut" }}
          >
            <Card3D isVisible={isInView} />
          </motion.div>
          {/* </motion.div> */}
          {/* <motion.div className="flex flex-col gap-4 items-center" initial={{ scale: 0.9, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} transition={{ duration: 0.8, ease: "easeOut" }} viewport={{ once: true }}>
            <CardPreview fullName="Test User" dateOfBirth="12/01/1990" borderColour="#0000FF" textColour="#FFFFFF" diabetesTextColour="#000000" />
          </motion.div> */}
          <div className="flex flex-col gap-14 md:basis-1/3">
            <AnimatedSmallCard direction="right" icon={faWallet} title="Standard Wallet Fit" desc="Fits perfectly in your wallet, with the outer edge visible to attract attention" align="text-end" />
            <AnimatedSmallCard direction="right" icon={faMoneyBill1} title="One-Time Purchase" desc="Your QR code is unique. Once purchased, you won't need to buy another!" align="text-end" />
            <AnimatedSmallCard direction="right" icon={faHeadset} title="Quick Support" desc="Get fast, dedicated support via Discord and email. Requests are prioritised to ensure quick and efficient resolutions." align="text-end" border={false} />
          </div>
        </motion.div>
      </Container>
      <div
        className="absolute w-full overflow-hidden pointer-events-none"
        style={{ zIndex: 0 }} // Ensure it's behind everything
      >
        <img
          src="/intersecting-waves-split-2.svg"
          alt="Wave"
        />
      </div>
    </div>
  );
};

const AnimatedHeading = ({ text }: { text: string }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.7 });
  return (
    <motion.h2 ref={ref} className="text-4xl text-center md:text-6xl font-bold" initial={{ opacity: 0, y: -30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2 }}>
      {text}
    </motion.h2>
  );
};

const AnimatedSmallCard = ({ direction, icon, title, desc, align, border = true }: { direction: "left" | "right"; icon: IconDefinition; title: string; desc: string; align: "text-start" | "text-end"; border?: boolean }) => (
  <motion.div className={`space-y-2 z-10 ${border && "border-b-2 pb-6 border-gray-200"} ${align}`} initial={{ opacity: 0, x: direction === "left" ? -50 : 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: "easeOut" }} viewport={{ once: true }}>
    <FontAwesomeIcon icon={icon} className="text-[#0101ff] md:mb-0 mb-2 text-2xl md:text-3xl" />
    <p className="font-bold text-black text-xl md:text-xl">{title}</p>
    <p className="text-sm md:text-lg">{desc}</p>
  </motion.div>
);

const AnimatedParagraphUp = ({ children }: { children: React.ReactNode }) => {
  return (
    <motion.div className="text-black text-md md:text-lg italic md:w-3/4 md:px-0 px-8" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: "easeOut" }} viewport={{ once: true }}>
      {children}
    </motion.div>
  );
};

const LandingPageCard = ({ icon, title, desc }: { icon: JSX.Element; title: string; desc: JSX.Element }) => {
  return (
    <motion.div className="bg-white shadow-md flex flex-col md:mx-0 mx-4 gap-6 rounded-3xl px-4 py-8 md:px-6 md:py-12 text-center hover:shadow-xl transition duration-300" whileHover={{ scale: 1.05 }}>
      {icon}
      <p className="md:text-3xl font-bold text-2xl text-black">{title}</p>
      <p className="text-black md:text-lg text-xl">{desc}</p>
    </motion.div>
  );
};